import * as Sentry from '@sentry/react'
import { isAxiosError } from 'axios'
import type { WeChatOAuthIntent } from './wxLoginScript'

export type WeChatAuthFailureStage =
  | 'authorize_api'
  | 'wx_script_load'
  | 'wx_login_construct'
  | 'wx_qr_mount'
  | 'wx_qr_widget_error'

export type WeChatAuthFailureExtra = {
  appId?: string
  redirectUriHost?: string
  redirectUriPath?: string
  iframeSrcSummary?: WxLoginIframeSummary
  httpStatus?: number
  responseMessage?: string
}

export type WxLoginIframeSummary = {
  appId?: string
  redirectUriHost?: string
  redirectUriPath?: string
}

export function summarizeWxLoginIframeSrc(src: string): WxLoginIframeSummary {
  try {
    const url = new URL(src)
    const appId = url.searchParams.get('appid') ?? undefined
    const redirectUriRaw = url.searchParams.get('redirect_uri')
    if (!redirectUriRaw) {
      return { appId }
    }

    const decodedRedirectUri = decodeURIComponent(redirectUriRaw)
    const redirectUrl = new URL(decodedRedirectUri)
    return {
      appId,
      redirectUriHost: redirectUrl.host,
      redirectUriPath: redirectUrl.pathname,
    }
  } catch {
    return {}
  }
}

function buildAxiosFailureExtra(error: unknown): WeChatAuthFailureExtra {
  if (!isAxiosError(error)) {
    return {}
  }

  const responseData = error.response?.data
  let responseMessage: string | undefined
  if (typeof responseData === 'string') {
    responseMessage = responseData
  } else if (
    responseData != null &&
    typeof responseData === 'object' &&
    'message' in responseData &&
    typeof responseData.message === 'string'
  ) {
    responseMessage = responseData.message
  }

  return {
    httpStatus: error.response?.status,
    responseMessage,
  }
}

export function reportWeChatAuthFailure(input: {
  stage: WeChatAuthFailureStage
  intent: WeChatOAuthIntent
  error: unknown
  extra?: WeChatAuthFailureExtra
}): void {
  const axiosExtra = buildAxiosFailureExtra(input.error)
  const extra = {
    ...axiosExtra,
    ...input.extra,
  }

  const message = `WeChat auth failed at stage: ${input.stage}`
  const errorForSentry =
    input.error instanceof Error ? input.error : new Error(message)

  console.error('[WeChatAuth]', message, {
    intent: input.intent,
    stage: input.stage,
    error: input.error,
    extra,
  })

  Sentry.captureException(errorForSentry, {
    tags: {
      feature: 'wechat-auth',
      stage: input.stage,
      intent: input.intent,
    },
    extra,
  })
}
